import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { takeUntil } from 'rxjs/operators';
import { PersonalNotificationsService } from '@fi-app/personal-notifications/personal-notifications.service';
import { IPersonalNotificationsItem } from '@fi-app/personal-notifications/personal-notifications';

@Component({
    selector: 'fi-personal-notifications-list',
    templateUrl: './personal-notifications-list.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./personal-notifications-list.component.scss'],
    host: { class: 'fi-personal-notifications-list' },
    standalone: false,
})
export class PersonalNotificationsListComponent implements OnInit, OnDestroy {
    @Output() newNotificationsCountChange = new EventEmitter<number>();
    @Output() afterPersonalNotificationClick = new EventEmitter<number>();
    @Output() closeNotificationList = new EventEmitter<void>();

    loading = false;
    items: IPersonalNotificationsItem[] = [];
    unvisitedNotificationsCount = 0;

    private offset = 0;
    private numberOfRecordsToTake = 20;
    private lastLoaded = false;
    private loadingPromise: IFiHttpPromise<IPersonalNotificationsItem[]>;
    private timeout;

    loadMoreDone = new BehaviorSubject(this.offset);

    constructor(
        private fiPersonalNotificationsService: PersonalNotificationsService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private elementRef: ElementRef,
    ) {}

    ngOnInit() {
        this.fiPersonalNotificationsService
            .getUnvisitedPersonalNotificationsCount()
            .then((cnt) => (this.unvisitedNotificationsCount = cnt));

        this.loadMore().then(() => {
            // mark all notifications as seen automatically after 5 seconds notifications list still opened
            this.timeout = setTimeout(() => {
                this.fiPersonalNotificationsService.markAllPersonalNotificationsAsSeen().then((response) => {
                    this.newNotificationsCountChange.emit(0);
                    this.items.forEach((item) => {
                        if (item.CategoryType == 0) item.CategoryType = 1;
                    });
                });
            }, 2000);
        });

        this.fiBrowserEventsService
            .getDocumentEventSubject$('click')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: Event) => {
                if (!this.elementRef.nativeElement.contains($event.target)) this.closeNotificationList.emit();
            });
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.loadingPromise.cancel();
        // clear timeout on list close
        clearTimeout(this.timeout);
    }

    loadMore() {
        if (this.lastLoaded) return;

        if (this.loading) return this.loadingPromise;

        this.loading = true;
        this.loadingPromise = this.fiPersonalNotificationsService.getPersonalNotifications(
            this.offset,
            this.numberOfRecordsToTake,
        );
        this.loadingPromise
            .then((response) => {
                if (response.length) {
                    this.items = this.items.concat(response);
                    this.offset = this.items.length;
                }
                if (response.length < this.numberOfRecordsToTake) this.lastLoaded = true;

                this.loadMoreDone.next(this.offset);
            })
            .finally(() => {
                this.loading = false;
            });
        return this.loadingPromise;
    }

    markAllPersonalNotificationsAsRead() {
        this.fiPersonalNotificationsService.markAllPersonalNotificationsAsVisited().then((response) => {
            this.newNotificationsCountChange.emit(0);
            this.items.forEach((item) => (item.CategoryType = 2));
            this.unvisitedNotificationsCount = 0;
        });
    }
}
